/**
 * Plan section 10 "Core: definition, stubs, submit example" — the first
 * target that emits a real, runnable @modyra/core form: `form.ts` (a
 * `createForm()` schema, not a description of one), `stubs.ts`
 * (typed, throwing placeholders for every symbolic ImplementationRef —
 * ADR-0005, , ), and, when the project declares a submit action,
 * `submit-example.ts` showing it wired to `form.submit()`.
 */
import { buildStubsModule, capabilityDiagnostics } from "@modyra/studio-codegen";
import { compileToContract } from "@modyra/studio-contract";
import { STUDIO_FIELD_KINDS } from "@modyra/studio-model";
import { buildFormModule } from "./field-mapper.js";
const CAPABILITIES = {
    fieldKinds: STUDIO_FIELD_KINDS,
    validatorKinds: ["required", "email", "min", "max", "minLength", "maxLength", "pattern", "oneOf", "eachOneOf", "customRef"],
    supportsArrays: true,
    supportsGroups: true,
    supportsServerValidators: true,
    supportsFormValidators: true,
};
function buildSubmitExample(project, stubNameFor) {
    const submitRef = project.behaviors.submit?.implementationRef;
    const stubName = submitRef ? stubNameFor.get(submitRef) : undefined;
    if (!stubName)
        return null;
    return `import { form } from "./form.js";\nimport { ${stubName} } from "./stubs.js";\n\nform.submit(${stubName});\n`;
}
function generateFiles(project) {
    const stubsResult = buildStubsModule(project);
    const formResult = buildFormModule(project, stubsResult.nameFor);
    // A project the contract compiler cannot express is a fact about the project rather than about the
    // one target that happens to ask: a field kind the catalog does not declare is an error there, and
    // a target that never asks answers `compatible: true` for it.
    //
    // Errors only. The compiler's warnings are about the *contract document* it is building — a server
    // validator with no Contract v2 equivalent is omitted from that document and carried into this
    // target's own output — so repeating them here would tell an author something was dropped that
    // this target emits.
    const diagnostics = [
        ...stubsResult.diagnostics,
        ...formResult.diagnostics,
        ...compileToContract(project).diagnostics.filter((d) => d.severity === "error"),
        ...capabilityDiagnostics(project, { id: "core", capabilities: CAPABILITIES }),
    ];
    if (!formResult.code)
        return { files: [], diagnostics };
    const files = [
        { path: "form.ts", language: "typescript", content: formResult.code, role: "source" },
        { path: "stubs.ts", language: "typescript", content: stubsResult.code, role: "source" },
    ];
    const submitCode = buildSubmitExample(project, stubsResult.nameFor);
    if (submitCode)
        files.push({ path: "submit-example.ts", language: "typescript", content: submitCode, role: "docs" });
    return { files, diagnostics };
}
export function createCoreTarget() {
    return {
        id: "core",
        displayName: "Core (createForm)",
        version: "0.1.0",
        capabilities: CAPABILITIES,
        defaults() {
            return {};
        },
        async analyze(project) {
            const { diagnostics } = generateFiles(project);
            return { compatible: !diagnostics.some((d) => d.severity === "error"), diagnostics };
        },
        async generate(project) {
            const { files, diagnostics } = generateFiles(project);
            return { targetId: "core", files, diagnostics, entryFile: files.length ? "form.ts" : undefined };
        },
    };
}
export const coreTargetManifest = {
    id: "core",
    displayName: "Core (createForm)",
    load: async () => createCoreTarget(),
};
